import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, User } from '../services/api';
import Loader from './Loader';
import EmptyState from './EmptyState';
import ConfirmModal from './ConfirmModal';
import { useNotifications } from './NotificationSystem';
import { handleApiError } from '../utils/errorHandler';
import './Auth.css';

const AdminDashboard: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activating, setActivating] = useState<string | null>(null);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; userId: string | null }>({
    isOpen: false,
    userId: null
  });
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const hasFetchedRef = useRef(false); // Prevent duplicate fetches
  const isFetchingRef = useRef(false); // Prevent concurrent requests

  const loadUsers = useCallback(async () => {
    // CRITICAL: Don't make API calls if not authenticated
    if (!apiService.isAuthenticated()) {
      console.warn('AdminDashboard: Not authenticated, skipping API calls');
      setLoading(false);
      navigate('/admin/login');
      return;
    }

    // Prevent duplicate/concurrent requests
    if (isFetchingRef.current) {
      console.log('AdminDashboard: Request already in progress, skipping');
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;

    try {
      setLoading(true);
      // Get all users for admin management
      const allUsers = await apiService.getUsers();
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setUsers(allUsers);
        setError('');
        hasFetchedRef.current = true;
      }
    } catch (err: any) {
      // Don't handle errors if component is unmounted
      if (!isMountedRef.current) {
        return;
      }

      // Only show error if not aborted
      if (err.name === 'AbortError') {
        return;
      }

      if (err.status === 401 || err.errorCode === 'UNAUTHORIZED' || err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        // Not authorized, redirect to admin login
        navigate('/admin/login');
      } else {
        const appError = handleApiError(err);
        setError(appError.message);
        showError('Loading Error', appError.message);
      }
    } finally {
      isFetchingRef.current = false;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only create once, use navigate/showError from closure

  useEffect(() => {
    isMountedRef.current = true;
    hasFetchedRef.current = false;
    isFetchingRef.current = false;
    
    // Only fetch once on mount if authenticated
    if (apiService.isAuthenticated() && !hasFetchedRef.current) {
      loadUsers();
    } else if (!apiService.isAuthenticated()) {
      setLoading(false);
      navigate('/admin/login');
    }

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      isFetchingRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  const handleActivate = async (userId: string) => {
    try {
      setActivating(userId);
      const response = await apiService.activateUser(userId);
      showSuccess('User Activated', response.message || 'User activated successfully');
      await loadUsers();
    } catch (err: any) {
      const appError = handleApiError(err);
      showError('Activation Error', appError.message);
    } finally {
      setActivating(null);
    }
  };

  const handleVerifyEmail = async (userId: string) => {
    try {
      setVerifying(userId);
      const response = await apiService.verifyUserEmail(userId);
      showSuccess('Email Verified', response.message || 'Email verified successfully');
      await loadUsers();
    } catch (err: any) {
      const appError = handleApiError(err);
      showError('Verification Error', appError.message);
    } finally {
      setVerifying(null);
    }
  };

  const handleDeactivateClick = (userId: string) => {
    setDeleteConfirm({ isOpen: true, userId });
  };

  const handleDeactivateConfirm = async () => {
    if (!deleteConfirm.userId) return;
    
    try {
      setActivating(deleteConfirm.userId);
      const response = await apiService.deactivateUser(deleteConfirm.userId);
      showSuccess('User Deactivated', response.message || 'User deactivated successfully');
      await loadUsers();
      setDeleteConfirm({ isOpen: false, userId: null });
    } catch (err: any) {
      const appError = handleApiError(err);
      showError('Deactivation Error', appError.message);
      setDeleteConfirm({ isOpen: false, userId: null });
    } finally {
      setActivating(null);
    }
  };


  const handleLogout = () => {
    apiService.logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <Loader message="Loading users..." size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, userId: null })}
        onConfirm={handleDeactivateConfirm}
        title="Deactivate User"
        message="Are you sure you want to deactivate this user? They will not be able to access their account."
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        confirmVariant="danger"
        loading={activating === deleteConfirm.userId}
      />
      
      <div className="auth-card admin-dashboard-card">
        <div className="auth-header admin-header">
          <div>
            <div className="auth-logo">
              <span className="logo-icon">💰</span>
              <h1>RokdaHisab Admin</h1>
            </div>
            <h2>User Management</h2>
            <p>Manage user accounts</p>
          </div>
          <button
            onClick={handleLogout}
            className="admin-logout-btn"
          >
            Logout
          </button>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '20px' }}>
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div style={{ marginTop: '30px' }}>
          {users.length === 0 ? (
            <EmptyState
              icon="👥"
              title="No users found"
              message="There are no users in the system yet."
            />
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Email Verified</th>
                    <th>Status</th>
                    <th className="hide-mobile">Company</th>
                    <th className="hide-mobile">Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isActive = user.isActive ?? true;
                    return (
                      <tr key={user.id}>
                        <td>
                          {user.firstName} {user.lastName}
                        </td>
                        <td>{user.email}</td>
                        <td className="text-center">
                          {user.emailVerified ? (
                            '✅'
                          ) : (
                            <button
                              onClick={() => handleVerifyEmail(user.id)}
                              disabled={verifying === user.id}
                              className="btn-verify"
                            >
                              {verifying === user.id ? 'Verifying...' : 'Verify Email'}
                            </button>
                          )}
                        </td>
                        <td className="text-center">
                          {isActive ? '🟢 Active' : '🔴 Inactive'}
                        </td>
                        <td className="hide-mobile">{user.company || '-'}</td>
                        <td className="hide-mobile">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="text-center">
                          {isActive ? (
                            <button
                              onClick={() => handleDeactivateClick(user.id)}
                              disabled={activating === user.id}
                              className="btn-deactivate"
                            >
                              {activating === user.id ? 'Deactivating...' : 'Deactivate'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(user.id)}
                              disabled={activating === user.id}
                              className="btn-activate"
                            >
                              {activating === user.id ? 'Activating...' : 'Activate'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

